/**
 * End times on itinerary tiles.
 *
 * `ends_at` has been on the row, in the API contract and validated server-side
 * ("End time must be on or after start time") since the timeline foundation.
 * No Studio field ever wrote one, so every tile a host has ever made is a start
 * time and nothing else — which is why the guest page showed "19:30" where it
 * should show "19:30 – 22:00", and why a calendar export would have to invent a
 * duration for every event on the weekend.
 *
 * Rendering lives in tripTime's `formatRangeInZone`, not here: it needs the
 * zone guard and the memoized formatters that module already owns, and a range
 * built by pasting two independently-formatted halves together puts the zone
 * name in the middle of it.
 */

/** What a save should do with the end-time box, given what is stored. */
export type EndTimeEdit =
  | { kind: 'send'; wallClock: string }
  | { kind: 'unchanged' }
  /** Cleared a stored value. The API cannot express this; say so, do not pretend. */
  | { kind: 'cannot_clear' };

/**
 * The one rule not visible from the endpoint: the upsert writes
 * `ends_at = COALESCE(@EndsAt, ends_at)`, so null means PRESERVE. A host can
 * set an end time and change it, but clearing the box does not remove it — the
 * save succeeds and the old value stays. Silently.
 */
export function endTimeEdit(draft: string, stored: string): EndTimeEdit {
  const next = draft.trim();
  const previous = stored.trim();
  if (next === previous) return { kind: 'unchanged' };
  // next !== previous and next is empty means previous was not: the clear.
  if (!next) return { kind: 'cannot_clear' };
  return { kind: 'send', wallClock: next };
}

/**
 * Minutes precision, which is the only precision this form has.
 *
 * The comparisons below are lexical — `YYYY-MM-DDTHH:mm` sorts correctly and
 * carries no zone, and parsing these into Dates is how an event ending at
 * midnight becomes one ending yesterday. But lexical only holds while both
 * sides are the same LENGTH: '...T19:30' sorts before '...T19:30:00', so a
 * value that arrived with seconds would read as earlier than the identical
 * time without them. A datetime-local with a sub-minute `step`, or a value
 * round-tripped through a source that keeps seconds, produces exactly that.
 */
function toMinutes(value: string): string {
  return value.trim().slice(0, 16);
}

/**
 * '' when the pair is acceptable.
 *
 * Mirrors the server's own check — "on or after" — so a host learns it while
 * the box is still focused rather than from a red banner after the round trip.
 * Deliberately no stricter than the server: refusing a zero-length event here
 * would make an existing tile with equal times permanently uneditable, because
 * this predicate also disables Save.
 */
export function endTimeProblem(startsAt: string, endsAt: string): string {
  const start = toMinutes(startsAt);
  const end = toMinutes(endsAt);
  if (!end) return '';
  if (!start) return 'Add a start time first — an end on its own says nothing.';
  if (end < start) return 'Ends before it starts. Check the day as well as the time.';
  return '';
}
